'use client';

import React from 'react';
import { CustomImage } from 'components/shared';
import { TEST_PANEL_PLANS } from './constants';
import { Panel } from './types';
import styles from './MobileBiomarkerTable.module.css';

const cx = (...classes: (string | undefined | false)[]) =>
  classes.filter(Boolean).join(' ');

const MobileBiomarkerTable = () => {
  const plans = TEST_PANEL_PLANS;
  const left = plans[0];
  const right = plans[1];

  // Build ordered labels with common panels first, followed by right-only then left-only
  const orderedLabels: string[] = React.useMemo(() => {
    const rightOrder = (right.panels || []).map((p) => p.label);
    const leftOrder = (left.panels || []).map((p) => p.label);
    const common = rightOrder.filter((l) => leftOrder.includes(l));
    const rightOnly = rightOrder.filter((l) => !leftOrder.includes(l));
    const leftOnly = leftOrder.filter((l) => !rightOrder.includes(l));
    // Order: common, essential-only (left), annual-only (right)
    return [...common, ...leftOnly, ...rightOnly];
  }, [left.panels, right.panels]);

  const [open, setOpen] = React.useState<Record<string, boolean>>(() => {
    const initialOpenMap: Record<string, boolean> = {};
    orderedLabels.slice(0, 2).forEach((label) => {
      initialOpenMap[label] = true;
    });
    return initialOpenMap;
  });

  const getPanel = (arr: Panel[] | undefined, label: string) =>
    arr?.find((p) => p.label === label);

  const extractBiomarkerCount = (panel?: Panel) =>
    panel?.available ? panel.availableBiomarkers?.length || 0 : undefined;

  const extractBiomarkers = (panel?: Panel) => ({
    available: panel?.availableBiomarkers || [],
    unavailable: panel?.unavailableBiomarkers || [],
  });

  return (
    <div className={styles.wrapper}>
      <table>
        <thead>
          <tr>
            <th>Compare tests</th>
            <th>{left.title?.replace(/\s*plan$/i, '') || 'Essential'}</th>
            <th>{right.title?.replace(/\s*plan$/i, '') || 'Annual'}</th>
          </tr>
        </thead>
        <tbody>
          <tr className={styles.summary}>
            <td>
              <span className={styles.rowLabel}>Total Tests</span>
            </td>
            <td className={styles.count}>60+</td>
            <td className={styles.count}>100+</td>
          </tr>
          <tr className={styles.summary}>
            <td>
              <span className={styles.rowLabel}>Total Panels</span>
            </td>
            <td className={styles.count}>8</td>
            <td className={styles.count}>17</td>
          </tr>

          {orderedLabels.map((label) => {
            const lPanel = getPanel(left.panels, label);
            const rPanel = getPanel(right.panels, label);
            const lCount = extractBiomarkerCount(lPanel);
            const rCount = extractBiomarkerCount(rPanel);
            const isInactive = !(lPanel?.available || rPanel?.available);
            const expanded = !!open[label];

            return (
              <React.Fragment key={label}>
                <tr
                  className={cx(
                    isInactive && styles.inactive,
                    expanded && styles.expanded,
                  )}
                  onClick={() => setOpen((s) => ({ ...s, [label]: !s[label] }))}
                >
                  <td>
                    <div className={styles.rowHeader}>
                      <div className={styles.thumb} aria-hidden>
                        <CustomImage
                          src={
                            lPanel?.imageUrl ||
                            rPanel?.imageUrl ||
                            'web_v2/thumb/uh-icon-1000x1000.png/w_64'
                          }
                          alt={`${label} biomarker panel`}
                          fill
                        />
                      </div>
                      <span className={styles.rowLabel}>{label}</span>
                      <span
                        aria-hidden
                        className={cx(styles.chevron, expanded && styles.open)}
                      >
                        {ChevronSvg}
                      </span>
                    </div>
                  </td>
                  <td className={styles.count}>
                    {lCount !== undefined ? lCount : '-'}
                  </td>
                  <td className={styles.count}>
                    {rCount !== undefined ? rCount : '-'}
                  </td>
                </tr>
                {expanded ? (
                  <tr className={styles.expandRow}>
                    <td colSpan={3}>
                      <div className={styles.expandContent}>
                        <div className={styles.chipsWrap}>
                          {(() => {
                            const leftBio = extractBiomarkers(lPanel);
                            const rightBio = extractBiomarkers(rPanel);
                            // Prioritize Essential-available biomarkers first (keeping Comprehensive order),
                            // then the rest from Comprehensive, followed by any unavailable items.
                            const combined: string[] = [];
                            const rightAvail = rightBio.available;
                            const leftAvail = leftBio.available;
                            const prioritized = rightAvail.filter((t) =>
                              leftAvail.includes(t),
                            );
                            const restRight = rightAvail.filter(
                              (t) => !leftAvail.includes(t),
                            );
                            const ordered = [
                              ...prioritized,
                              ...restRight,
                              ...rightBio.unavailable,
                              ...leftBio.unavailable,
                            ];
                            ordered.forEach((t) => {
                              if (!combined.includes(t)) combined.push(t);
                            });

                            // Show marker only for biomarkers AVAILABLE in Essential plan
                            const essentialSet = new Set(leftBio.available);

                            return combined.map((b) => (
                              <span
                                className={styles.chip}
                                key={`agg-${label}-${b}`}
                              >
                                {essentialSet.has(b) ? (
                                  <span className={styles.dot} aria-hidden />
                                ) : null}
                                {b}
                              </span>
                            ));
                          })()}
                        </div>
                        <p className={styles.disclaimer}>
                          <span className={styles.dot} aria-hidden /> Biomarkers
                          in Essential plans
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ChevronSvg = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17"
    height="17"
    viewBox="0 0 17 17"
    fill="none"
  >
    <path
      d="M12.0166 10.8438L8.0166 6.84375L4.0166 10.8438"
      stroke="black"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default MobileBiomarkerTable;
